import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import { Autocomplete, styled, TextField } from "@mui/material";

const CustomTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "var(--inputs)",
    color: "var(--black)",
    fontSize: "0.9rem",
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(0.5),
    minHeight: "40px",
    padding: "8px",
    overflow: "hidden",
    "& fieldset": {
      border: "none",
    },
  },
  "& .MuiInputLabel-root": {
    position: "static",
    transform: "none",
    fontSize: "0.75rem",
    color: "var(--secondary-text)",

    marginBottom: theme.spacing(0.5),
  },
  "& .MuiInputLabel-root.Mui-error": {
    color: "#f44336",
  },
  "& .MuiIconButton-root": {
    color: "var(--black)",
  },
  "& .MuiAutocomplete-popupIndicator": {
    padding: "14px",
    marginRight: "0",
    borderRadius: 0,
    borderTopRightRadius: "12px",
    borderBottomRightRadius: "12px",
    transform: "translate(10px, 0px)",
    background: "var(--input-gradient)",
    color: "var(--black)",
  },
  '& .MuiInputBase-input:disabled': {
      cursor: 'no-drop',
    },
}));

const CustomAutoCompleteDropdown = ({ properties, methods }) => {
  const {
    name,
    label = "",
    dropDownList,
    isDisabled,
    isDisableClearable,
    placeholder,
    customOnChange,
    className,
    validation,
    required,
    readOnly,
    keepOpen = false,
    isPlaceholder = true,
  } = properties;

  const { control, setValue } = methods;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (keepOpen) {
      if (dropDownList && dropDownList.length > 0) {
        setOpen(true);
      } else {
        setOpen(false);
      }
    }
  }, [dropDownList, keepOpen]);

  const customRules = {
    ...validation,
    required: {
      value: required || false,
      message:
        label.charAt(0).toUpperCase() +
        label.slice(1).toLowerCase() +
        " " +
        t("Common.Is Required"),
    },
  };

  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({
          field: { ref, value, onChange, onBlur, ...field },
          fieldState: { error },
        }) => (
          <Autocomplete
            {...field}
            readOnly={readOnly}
            disabled={isDisabled}
            options={dropDownList || []}
            getOptionLabel={(option) => option?.label}
            value={
              dropDownList?.find((option) => option?.value === value) ||
              dropDownList?.find((option) => option?.value === value?.value) ||
              null
            }
            onChange={(event, newValue) => {
              const finalValue = newValue || null;
              onChange(finalValue);
              setValue(name, finalValue, { shouldValidate: true });
              if (customOnChange) {
                customOnChange(newValue);
              }
              setOpen(false);
            }}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => {
              setOpen(false);
            }}
            disableClearable={isDisableClearable}
            onBlur={onBlur}
            renderInput={(params) => (
              <CustomTextField
                {...params}
                size={"medium"}
                id={`${name}-label`}
                inputRef={ref}
                placeholder={
                  isPlaceholder
                    ? placeholder || "Please select value"
                    : undefined
                }
                label={label}
                error={!!error}
                helperText={error ? error?.message : null}
                required={validation?.required?.value || required}
                className={className}
                variant={"outlined"}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            )}
          />
        )}
        rules={customRules}
      />
    </>
  );
};

export default CustomAutoCompleteDropdown;